import { useState, useMemo } from "react";
import { ClipboardCheck, Paperclip, EyeOff, CheckCircle2, RotateCcw, AlertCircle, User, ChevronRight, Sparkles, Loader2 } from "lucide-react";
import Modal from "../components/Modal";
import Badge from "../components/Badge";
import { getSignedUrl } from "../lib/storage";
import { fmtShort } from "../utils";
import { QUALIDADES, pendentesDeConferencia, aplicarAvaliacao } from "../lib/conferencia";
import { analisarAbordagem, CORES_QUALIDADE, resumoCurto } from "../lib/analiseIa";

// ═══════════════════════════════════════════════════════════════
// FILA DE CONFERÊNCIA — o gerente avalia em série
// ═══════════════════════════════════════════════════════════════
//
// A conferência por card funciona para um caso; para 350 parceiros, obriga a
// caçar quem tem pendência. Aqui é fila corrida: abre, avalia, próximo. A
// ordem é a do mais antigo primeiro — o registro que está esperando há mais
// tempo é o que perdeu mais valor parado.

const abrirPrint = async (path) => {
  const win = window.open("", "_blank");
  const url = await getSignedUrl(path);
  if (!url) { if (win) win.close(); return; }
  if (win) win.location.href = url; else window.open(url, "_blank");
};

const ROTULO = { contato: "Contato", aceite: "Aceite", recusa: "Recusa", justificativa: "Espera" };

// Como a conversa soou. "Mecânica" não é implicância de estilo: é a conversa
// que o fornecedor não lê até o fim e não responde.
const TOM = {
  humana: { rotulo: "conversa humana", cor: "#059669" },
  mista: { rotulo: "meio engessada", cor: "#d97706" },
  robotica: { rotulo: "soou mecânica", cor: "#dc2626" },
};

export default function FilaConferencia({ open, partners = [], setPartners, userName = "", onClose, onAbrirEmpresa }) {
  const [i, setI] = useState(0);
  const [qualidade, setQualidade] = useState("");
  const [comentario, setComentario] = useState("");
  const [erro, setErro] = useState("");
  const [avaliadosAgora, setAvaliadosAgora] = useState(0);
  const [analises, setAnalises] = useState({});   // ref → parecer da IA
  const [analisando, setAnalisando] = useState(false);

  // A fila é recalculada a cada avaliação: o item avaliado sai sozinho, e o
  // índice continua apontando para o próximo da vez.
  const fila = useMemo(() => {
    const itens = [];
    for (const p of partners) {
      for (const ev of pendentesDeConferencia(p)) itens.push({ ...ev, parceiro: p });
    }
    return itens.sort((a, b) => (a.data || "").localeCompare(b.data || ""));
  }, [partners]);

  if (!open) return null;

  const atual = fila[Math.min(i, Math.max(fila.length - 1, 0))] || null;

  const avaliar = (veredito) => {
    if (!atual) return;
    if (!qualidade) { setErro("Diga como o trabalho foi feito."); return; }
    if (veredito === "refazer" && !comentario.trim()) { setErro("Escreva o que precisa ser corrigido."); return; }
    setPartners((prev) => prev.map((p) => (
      p.id === atual.parceiro.id
        ? aplicarAvaliacao(p, { ref: atual.ref, veredito, qualidade, comentario, autor: userName })
        : p
    )));
    setQualidade(""); setComentario(""); setErro("");
    setAvaliadosAgora((n) => n + 1);
    setI(0); // a fila encolhe: volta para o topo do que sobrou
  };

  const pular = () => { setQualidade(""); setComentario(""); setErro(""); setI((n) => n + 1); };

  // O parecer da IA é opinião assinada, não veredito: ela lê o print e o texto
  // antes do gerente, e ele decide. Sugere a qualidade, nunca grava sozinha.
  const analisar = async () => {
    if (!atual) return;
    setAnalisando(true);
    const r = await analisarAbordagem(atual.parceiro.id, atual.ref);
    setAnalisando(false);
    setAnalises((p) => ({ ...p, [atual.ref]: r.ok ? r.analise : { erro: r.erro } }));
    if (r.ok && r.analise?.qualidade && !qualidade) setQualidade(r.analise.qualidade);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={fila.length ? `Fila de conferência — ${fila.length} ${fila.length === 1 ? "registro" : "registros"}` : "Fila de conferência"}
      w={620}
    >
      {!atual && (
        <div style={{ padding: "28px 14px", textAlign: "center" }}>
          <CheckCircle2 size={30} color="#059669" style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>Fila vazia.</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
            {avaliadosAgora > 0
              ? `${avaliadosAgora} ${avaliadosAgora === 1 ? "registro conferido" : "registros conferidos"} agora. Tudo em dia.`
              : "Todo registro do comercial já foi conferido."}
          </div>
        </div>
      )}

      {atual && (
        <>
          <div style={{ display: "flex", gap: 9, padding: "10px 12px", borderRadius: 9, background: "#f5f3ff", border: "1px solid #ddd6fe", marginBottom: 12 }}>
            <ClipboardCheck size={15} color="#6d28d9" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 11, color: "#5b21b6", lineHeight: 1.45 }}>
              Conferindo o mais antigo primeiro. Avaliar <strong>não muda o estágio</strong> da empresa —
              fica na trilha com seu nome e volta para quem executou.
              {avaliadosAgora > 0 && <> Já foram <strong>{avaliadosAgora}</strong> nesta sessão.</>}
            </div>
          </div>

          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <button
                  onClick={() => onAbrirEmpresa && onAbrirEmpresa(atual.parceiro)}
                  style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 14, fontWeight: 800, color: "#0f172a" }}
                  title="Abrir a empresa"
                >
                  {atual.parceiro.name} <ChevronRight size={13} color="#94a3b8" />
                </button>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", marginTop: 4 }}>{atual.titulo}</div>
                <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span>{fmtShort(atual.data)}</span>
                  {atual.contatoEm && <span>às {String(atual.contatoEm).slice(11, 16)}</span>}
                  {atual.autor && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><User size={10} /> {atual.autor}</span>}
                  {atual.diasDeAtraso > 0 && (
                    <span
                      title="A conversa é de um dia e o registro foi feito em outro. A meta conta pelo registro."
                      style={{ fontWeight: 700, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", padding: "1px 7px", borderRadius: 10 }}
                    >
                      conversa de {fmtShort(atual.diaDaConversa)} · lançada {atual.diasDeAtraso}d depois
                    </span>
                  )}
                </div>
              </div>
              <Badge color="#7c3aed" small>{ROTULO[atual.tipo] || atual.tipo}</Badge>
            </div>

            {atual.detalhe && (
              <div style={{ fontSize: 11.5, color: "#475569", marginTop: 8, lineHeight: 1.45, background: "#f8fafc", borderRadius: 7, padding: "8px 10px" }}>
                {atual.detalhe}
              </div>
            )}

            {atual.tipo === "contato" && (
              <div style={{ marginTop: 8 }}>
                {atual.comprovante ? (
                  <button onClick={() => abrirPrint(atual.comprovante)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px", borderRadius: 6, border: "1px solid #99f6e4", background: "#f0fdfa", color: "#0f766e", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                    <Paperclip size={11} /> Ver comprovante da conversa
                  </button>
                ) : (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, color: "#b45309", fontWeight: 600 }}>
                    <EyeOff size={11} /> registrado sem comprovante
                  </span>
                )}
              </div>
            )}

            {/* Parecer da IA — lê o print e o texto antes do gerente */}
            {(() => {
              const a = analises[atual.ref] || atual.analiseIa;
              if (!a) {
                return (
                  <button
                    onClick={analisar} disabled={analisando}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 10, padding: "6px 12px", borderRadius: 7, border: "1px solid #ddd6fe", background: "#f5f3ff", color: "#6d28d9", fontSize: 11, fontWeight: 600, cursor: analisando ? "wait" : "pointer" }}
                  >
                    {analisando ? <><Loader2 size={12} className="girando" /> Lendo a conversa...</> : <><Sparkles size={12} /> Analisar com IA</>}
                  </button>
                );
              }
              if (a.erro) return <div style={{ marginTop: 10, fontSize: 10.5, color: "#b45309" }}>IA: {a.erro}</div>;
              const cor = CORES_QUALIDADE[a.qualidade] || "#64748b";
              return (
                <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8, background: "#faf5ff", border: "1px solid #e9d5ff" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                    <Sparkles size={12} color="#7c3aed" />
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#6d28d9" }}>Leitura da IA</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: cor, background: `${cor}14`, padding: "1px 7px", borderRadius: 10 }}>{a.qualidade}</span>
                    <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: "auto" }}>{resumoCurto(a)}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: "#475569", lineHeight: 1.45 }}>{a.resumo}</div>
                  {a.data_no_print && (
                    <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 4 }}>
                      No print: <strong>{a.data_no_print}</strong>
                      {a.confere_com_informado === false && <span style={{ color: "#dc2626" }}> — não bate com a data informada</span>}
                      {a.confere_com_informado === true && <span style={{ color: "#059669" }}> — bate com o informado</span>}
                    </div>
                  )}
                  {/* Como a conversa soou. Texto colado e bloco gigante são
                      conferidos por string, não pelo modelo — por isso vêm
                      afirmados, sem "talvez". */}
                  {(a.humanizacao || (a.colagem || []).length > 0 || a.mensagens_longas > 0) && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                      {a.humanizacao && (
                        <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 7px", borderRadius: 10, color: TOM[a.humanizacao]?.cor || "#64748b", background: `${TOM[a.humanizacao]?.cor || "#64748b"}14` }}>
                          {TOM[a.humanizacao]?.rotulo || a.humanizacao}
                        </span>
                      )}
                      {(a.colagem || []).length > 0 && (
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: "#b45309", background: "#fffbeb", padding: "2px 7px", borderRadius: 10 }}>
                          colou o texto padrão
                        </span>
                      )}
                      {a.mensagens_longas > 0 && (
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: "#b45309", background: "#fffbeb", padding: "2px 7px", borderRadius: 10 }}>
                          {a.mensagens_longas} {a.mensagens_longas === 1 ? "mensagem longa demais" : "mensagens longas demais"}
                        </span>
                      )}
                    </div>
                  )}

                  {/* O roteiro da gerência, ponto a ponto. Só aparece o que
                      faltou — listar o que deu certo empurraria o que importa
                      para fora da tela. */}
                  {(a.pontos || []).filter((p) => p.situacao === "nao_cobriu" || p.situacao === "parcial").length > 0 && (
                    <div style={{ fontSize: 10.5, color: "#475569", marginTop: 6 }}>
                      Do roteiro, ficou faltando:{" "}
                      {a.pontos.filter((p) => p.situacao === "nao_cobriu" || p.situacao === "parcial")
                        .map((p) => `${p.observacao || p.id}${p.situacao === "parcial" ? " (pela metade)" : ""}`)
                        .join("; ")}
                    </div>
                  )}

                  {(a.alertas || []).length > 0 && (
                    <ul style={{ margin: "6px 0 0", paddingLeft: 16, fontSize: 10.5, color: "#b45309", lineHeight: 1.5 }}>
                      {a.alertas.map((x, k) => <li key={k}>{x}</li>)}
                    </ul>
                  )}
                  {a.sugestao && (
                    <div style={{ fontSize: 10.5, color: "#0f766e", marginTop: 6 }}>Para o vendedor: {a.sugestao}</div>
                  )}
                  <div style={{ fontSize: 9.5, color: "#94a3b8", marginTop: 6 }}>
                    Opinião automática — quem confirma ou manda refazer é você.
                  </div>
                </div>
              );
            })()}

            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 12 }}>
              {QUALIDADES.map((q) => (
                <button
                  key={q.id} title={q.dica}
                  onClick={() => { setQualidade(q.id); setErro(""); }}
                  style={{
                    padding: "6px 12px", borderRadius: 6, fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                    border: `1px solid ${qualidade === q.id ? q.cor : "#e2e8f0"}`,
                    background: qualidade === q.id ? `${q.cor}14` : "#fff",
                    color: qualidade === q.id ? q.cor : "#64748b",
                  }}
                >
                  {q.label}
                </button>
              ))}
            </div>

            <input
              value={comentario}
              onChange={(e) => { setComentario(e.target.value); setErro(""); }}
              placeholder="Comentário para o comercial (obrigatório se for refazer)"
              style={{ width: "100%", marginTop: 9, padding: "8px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 11.5, boxSizing: "border-box", color: "#334155", fontFamily: "inherit" }}
            />

            {erro && (
              <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "#dc2626", marginTop: 6 }}>
                <AlertCircle size={11} /> {erro}
              </div>
            )}

            <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
              <button onClick={() => avaliar("confirmado")} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 15px", borderRadius: 7, border: "none", background: "#059669", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                <CheckCircle2 size={13} /> Confirmar
              </button>
              <button onClick={() => avaliar("refazer")} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 15px", borderRadius: 7, border: "1px solid #fecaca", background: "#fff", color: "#b91c1c", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                <RotateCcw size={13} /> Pedir para refazer
              </button>
              <button onClick={pular} style={{ padding: "8px 13px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer", marginLeft: "auto" }}>
                Ver depois
              </button>
            </div>
          </div>
        </>
      )}

      <style>{`.girando { animation: girar 1s linear infinite } @keyframes girar { to { transform: rotate(360deg) } }`}</style>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12, borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
        <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          Fechar
        </button>
      </div>
    </Modal>
  );
}
